import {
  PapiDaSharedOriginalsCreate,
  PapiDaSharedOriginalsUpdate,
  PapiRequestDaSharedOriginalsCreate,
  PapiRequestDaSharedOriginalsUpdate,
  PapiDaSharedOriginalsPublish,
  PapiDaSharedDeviationSubmitInit,
} from '@wix/da-papi-types';
import { SagaIterator } from 'redux-saga';
import i18next from 'i18next';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { isApiError, requestNapi, requestPuppy } from '@wix/da-http-client';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import { getContentFromStorage } from '@wix/da-editor-tiptap/pkg/components/utils/tiptap';
import { SchemaVersion } from '@wix/da-editor-tiptap/pkg/server/types';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import {
  createOriginalDeviation,
  editOriginalDeviation,
  publishOriginalDeviation,
  updateOriginalDeviation,
} from '../actions/originals';
import {
  OriginalDeviationFieldName,
  OriginalDeviationFormFields,
} from '../../modals/OriginalDeviationPopup/types';

function* throwError(errorMessage: string) {
  yield putErrorNotification(errorMessage);
}

function formDataToRequestData(
  formData: OriginalDeviationFormFields
): PapiRequestDaSharedOriginalsCreate | PapiRequestDaSharedOriginalsUpdate {
  const formDescription = formData[OriginalDeviationFieldName.DESCRIPTION];
  const formQuestions = formData[OriginalDeviationFieldName.QUESTIONS] ?? [];
  const questions = formQuestions.map(question => ({
    questionid: question.questionId,
    editorRaw: question.questionEditor
      ? question.questionEditor()
      : JSON.stringify({
          version: SchemaVersion.V1,
          document: getContentFromStorage(question.question),
        }),
    type: question.type,
    required: question.isRequired,
    answers:
      question.answers &&
      question.answers.map(answer => ({
        answerid: answer.answerId,
        label: answer.answer,
        price_modifier: answer.priceModifier || undefined,
      })),
  }));
  return {
    title: formData[OriginalDeviationFieldName.TITLE],
    is_mature: formData[OriginalDeviationFieldName.MATURE_CONTENT],
    noai: formData[OriginalDeviationFieldName.NO_AI],
    is_ai_generated: formData[OriginalDeviationFieldName.IS_AI_GENERATED],
    tags: formData[OriginalDeviationFieldName.TAGS],
    add_watermark: formData[OriginalDeviationFieldName.ADD_WATERMARK],
    show_in_progress_count:
      formData[OriginalDeviationFieldName.PROGRESS_IS_VISIBLE],
    display_resolution: Number(
      formData[OriginalDeviationFieldName.DISPLAY_RESOLUTION]
    ) as any,
    base_price: formData[OriginalDeviationFieldName.PRICE],
    delivery_time: formData[OriginalDeviationFieldName.DELIVERY_TIME],
    editorRaw: formDescription && formDescription(),
    questions,
  };
}

function* createOriginalDeviationSaga(
  action: ReturnType<typeof createOriginalDeviation>
): SagaIterator {
  const { formData, onSuccess, onError } = action.payload;
  const response: PapiDaSharedOriginalsCreate = yield call(requestNapi, {
    method: 'post',
    url: `/originals/create`,
    data: formDataToRequestData(formData),
  });

  if (!response || isApiError(response)) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.writeDeviationServerError')
    );
    if (onError) {
      yield call(onError, response);
    }
    return;
  }

  if (onSuccess) {
    yield call(onSuccess, response.deviation);
  }
}

function* updateOriginalDeviationSaga(
  action: ReturnType<typeof updateOriginalDeviation>
): SagaIterator {
  const { deviationId, formData, onSuccess, onError } = action.payload;
  const response: PapiDaSharedOriginalsUpdate = yield call(requestNapi, {
    method: 'post',
    url: `/originals/update`,
    data: {
      deviationid: deviationId,
      ...formDataToRequestData(formData),
    },
  });

  if (!response || isApiError(response)) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.writeDeviationServerError')
    );
    if (onError) {
      yield call(onError, response);
    }
    return;
  }

  if (onSuccess) {
    yield call(onSuccess, response.deviation);
  }
}

function* publishOriginalDeviationSaga(
  action: ReturnType<typeof publishOriginalDeviation>
): SagaIterator {
  const { deviationId, onSuccess, onError } = action.payload;
  const response: PapiDaSharedOriginalsPublish = yield call(requestNapi, {
    method: 'post',
    url: `/originals/publish`,
    data: {
      deviationid: deviationId,
    },
  });

  if (!response || isApiError(response)) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.publishDeviationServerError')
    );
    if (onError) {
      yield call(onError, response);
    }
    return;
  }

  if (onSuccess) {
    yield call(onSuccess);
  }
}

function* editOriginalDeviationSaga(
  action: ReturnType<typeof editOriginalDeviation>
): SagaIterator {
  const { deviationId } = action.payload;

  // Fetch the full init data for the original deviation
  const response: PapiDaSharedDeviationSubmitInit = yield call(requestPuppy, {
    method: 'get',
    url: '/deviation/submit/init',
    params: { deviationid: deviationId },
  });

  if (!response || isApiError(response)) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.initDeviationServerError')
    );
    return;
  }

  // Open the original deviation popup with the fetched init data
  yield put(
    pushModal(ModalType.ORIGINAL_DEVIATION, {
      wrapInModal: false,
      params: {
        initResponse: response,
      },
    })
  );
}

export default function* originalsSaga() {
  yield all([
    takeEvery(createOriginalDeviation, createOriginalDeviationSaga),
    takeEvery(updateOriginalDeviation, updateOriginalDeviationSaga),
    takeEvery(publishOriginalDeviation, publishOriginalDeviationSaga),
    takeEvery(editOriginalDeviation, editOriginalDeviationSaga),
  ]);
}
